/**
 * Platform › Embedding profiles: an embedder plus its Qdrant collection generation, shared by every bot on it. Pure
 * helpers for the page (form ↔ API body, validation, labels); tested in tests/embeddingProfilesView.test.ts.
 *
 * Rules the server enforces too (owner decisions 2026-10-16): id, dim and collection suffix are fixed after create;
 * a profile can be deleted only while no bot uses it and it is not the default.
 */

export interface EmbeddingProfileRow {
  id: string;
  label: string;
  embedderUrl: string;
  modelName: string;
  dim: number;
  collectionSuffix: string;
  initThreshold: number;
  finalThreshold: number;
  maxResults: number;
  multiVectorBoost: number;
  blockOnlyScore: number | null;
  blockAttachScore: number | null;
  isDefault: boolean;
  updatedBy: string | null;
  updatedAt: string;
  botCount: number;
}

export interface CollectionStatus {
  owner: string;
  name: string;
  exists: boolean | null;
  error?: string;
}

export interface ProbeResult {
  ok: boolean;
  dim: number | null;
  latencyMs: number;
  error?: string;
}

/** Text fields as typed; numbers stay strings until submit so a half-typed "0." is not lost. */
export interface ProfileForm {
  id: string;
  label: string;
  embedderUrl: string;
  modelName: string;
  dim: string;
  collectionSuffix: string;
  initThreshold: string;
  finalThreshold: string;
  maxResults: string;
  multiVectorBoost: string;
  blockOnlyScore: string;
  blockAttachScore: string;
  isDefault: boolean;
}

export type FormErrors = Partial<Record<keyof ProfileForm, string>>;

/** What the runtime uses when a block threshold is empty (env unset). */
export const BLOCK_DEFAULTS = { blockOnlyScore: 0.8, blockAttachScore: 0.6 } as const;

/** A new profile starts from bge-m3's measured search values (BGE_M3_MIGRATION.md §4.2). */
export function emptyForm(): ProfileForm {
  return {
    id: '', label: '', embedderUrl: '', modelName: '', dim: '', collectionSuffix: '',
    initThreshold: '0.45', finalThreshold: '0.35', maxResults: '10', multiVectorBoost: '0.2',
    blockOnlyScore: '', blockAttachScore: '', isDefault: false,
  };
}

const str = (v: number | null) => (v === null || v === undefined ? '' : String(v));

export function formFromRow(r: EmbeddingProfileRow): ProfileForm {
  return {
    id: r.id, label: r.label, embedderUrl: r.embedderUrl, modelName: r.modelName, dim: String(r.dim),
    collectionSuffix: r.collectionSuffix, initThreshold: str(r.initThreshold), finalThreshold: str(r.finalThreshold),
    maxResults: str(r.maxResults), multiVectorBoost: str(r.multiVectorBoost), blockOnlyScore: str(r.blockOnlyScore),
    blockAttachScore: str(r.blockAttachScore), isDefault: r.isDefault,
  };
}

const num = (s: string) => (s.trim() === '' ? NaN : Number(s));
const inUnit = (s: string) => Number.isFinite(num(s)) && num(s) >= 0 && num(s) <= 1;

export function validateForm(f: ProfileForm, mode: 'create' | 'edit'): FormErrors {
  const e: FormErrors = {};
  if (mode === 'create') {
    if (!/^[a-z0-9_]{2,32}$/.test(f.id)) e.id = '2–32 of a-z, 0-9, _';
    const dim = num(f.dim);
    if (!Number.isInteger(dim) || dim < 8 || dim > 8192) e.dim = 'A whole number, 8–8192';
    if (!/^(_[a-z0-9_]{1,31})?$/.test(f.collectionSuffix)) e.collectionSuffix = 'Empty, or _ followed by a-z, 0-9, _';
  }
  if (!f.label.trim()) e.label = 'Enter a name';
  const url = f.embedderUrl.trim();
  if (!/^https?:\/\/\S+$/.test(url)) e.embedderUrl = 'An http(s) URL';
  else if (/\/v1\/embeddings\/?$/.test(url)) e.embedderUrl = 'The base URL only — /v1/embeddings is added by the hub';
  if (!f.modelName.trim()) e.modelName = 'Enter the model name';
  for (const k of ['initThreshold', 'finalThreshold', 'multiVectorBoost'] as const) {
    if (!inUnit(f[k])) e[k] = '0–1';
  }
  const max = num(f.maxResults);
  if (!Number.isInteger(max) || max < 1 || max > 100) e.maxResults = '1–100';
  for (const k of ['blockOnlyScore', 'blockAttachScore'] as const) {
    if (f[k].trim() !== '' && (!inUnit(f[k]) || num(f[k]) < 0.01)) e[k] = 'Empty, or 0.01 to 1';
  }
  if (!e.blockOnlyScore && !e.blockAttachScore && f.blockOnlyScore.trim() && f.blockAttachScore.trim()
    && num(f.blockAttachScore) > num(f.blockOnlyScore)) {
    e.blockAttachScore = 'Not above the block-only score';
  }
  return e;
}

const block = (s: string) => (s.trim() === '' ? null : Number(s));

/** POST body for a new profile. */
export function createBody(f: ProfileForm): Record<string, unknown> {
  return {
    id: f.id.trim(), label: f.label.trim(), embedderUrl: f.embedderUrl.trim(), modelName: f.modelName.trim(),
    dim: Number(f.dim), collectionSuffix: f.collectionSuffix.trim(),
    initThreshold: Number(f.initThreshold), finalThreshold: Number(f.finalThreshold), maxResults: Number(f.maxResults),
    multiVectorBoost: Number(f.multiVectorBoost), blockOnlyScore: block(f.blockOnlyScore),
    blockAttachScore: block(f.blockAttachScore), isDefault: f.isDefault,
  };
}

/** PUT body: only what changed, never the fixed fields. Empty = nothing to save. */
export function updateBody(before: EmbeddingProfileRow, f: ProfileForm): Record<string, unknown> {
  const next: Record<string, unknown> = {
    label: f.label.trim(), embedderUrl: f.embedderUrl.trim(), modelName: f.modelName.trim(),
    initThreshold: Number(f.initThreshold), finalThreshold: Number(f.finalThreshold), maxResults: Number(f.maxResults),
    multiVectorBoost: Number(f.multiVectorBoost), blockOnlyScore: block(f.blockOnlyScore),
    blockAttachScore: block(f.blockAttachScore),
  };
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(next)) {
    if ((before as unknown as Record<string, unknown>)[k] !== v) out[k] = v;
  }
  if (f.isDefault && !before.isDefault) out.isDefault = true;
  return out;
}

/** The server asks for this before changing the model of a profile bots use (409 `model_change_needs_confirm`). */
export function needsModelConfirm(status: number, body: string): string | null {
  if (status !== 409) return null;
  try {
    const j = JSON.parse(body) as { code?: unknown; message?: unknown };
    return j.code === 'model_change_needs_confirm' && typeof j.message === 'string' ? j.message : null;
  } catch {
    return null;
  }
}

/** A save that changes the embedder is probed by the server first; the page says so on the button. */
export function changesEmbedder(before: EmbeddingProfileRow, f: ProfileForm): boolean {
  return before.embedderUrl !== f.embedderUrl.trim() || before.modelName !== f.modelName.trim();
}

/** "0.80" when set; "0.80 · default" when the profile leaves it to env / the built-in value. */
export function blockScoreLabel(v: number | null, which: keyof typeof BLOCK_DEFAULTS): string {
  return v === null ? `${BLOCK_DEFAULTS[which].toFixed(2)} · default` : v.toFixed(2);
}

/** Why Delete is disabled, or null when it is allowed. */
export function deleteBlockedReason(r: EmbeddingProfileRow): string | null {
  if (r.isDefault) return 'The default profile cannot be deleted — make another profile the default first';
  if (r.botCount > 0) return `${r.botCount} bot${r.botCount === 1 ? '' : 's'} use this profile — move them first`;
  return null;
}

export function botCountLabel(r: EmbeddingProfileRow): string {
  const n = `${r.botCount} bot${r.botCount === 1 ? '' : 's'}`;
  return r.isDefault ? `${n} + bots with no AI config yet` : n;
}

export function probeMessage(p: ProbeResult, wantDim: number): { tone: 'green' | 'red'; text: string } {
  if (p.ok) return { tone: 'green', text: `Answered in ${p.latencyMs} ms with ${p.dim}-dim vectors` };
  if (p.dim !== null && p.dim !== wantDim) {
    return { tone: 'red', text: `Answers with ${p.dim}-dim vectors; this profile needs ${wantDim}` };
  }
  return { tone: 'red', text: `No answer: ${p.error ?? 'unknown error'}` };
}

export function collectionsSummary(list: CollectionStatus[]): { tone: 'green' | 'amber' | 'red'; text: string } {
  const failed = list.filter((c) => c.error);
  const missing = list.filter((c) => !c.error && c.exists === false);
  const unknown = list.filter((c) => !c.error && c.exists === null);
  const ready = list.filter((c) => c.exists === true);
  if (failed.length || missing.length) {
    return { tone: 'red', text: `${ready.length} ready, ${missing.length + failed.length} missing — try again` };
  }
  if (unknown.length) return { tone: 'amber', text: `${ready.length} ready, ${unknown.length} could not be checked` };
  return { tone: 'green', text: `All ${ready.length} collections ready` };
}

/** The hub's refusals in plain words (body = Nest JSON `{message}` or text). */
export function saveError(status: number, body: string): string {
  if (status === 403) return 'Only global admins can change embedding profiles';
  if (status === 0) return `Could not reach the hub: ${body}`;
  let message = body;
  try {
    const j = JSON.parse(body) as { message?: unknown };
    if (Array.isArray(j.message)) message = j.message.join('; ');
    else if (typeof j.message === 'string') message = j.message;
  } catch {
    /* not JSON */
  }
  return message || `Request failed (${status})`;
}
